import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Trash2, Check, X, Pencil, ScanLine, Camera, AlertTriangle } from 'lucide-react';
import {
  getNutritionDiary, getLatestNutritionProfileReport,
  addNutritionEntry, updateNutritionEntry, deleteNutritionEntry, lookupBarcode,
  getPlayerMealToday, logMealScoop, getFoodClassification,
  checkIngredientLines, parseRecipeIngredients,
  getMealTemplates, saveMealTemplate, renameMealTemplate, deleteMealTemplate,
} from '../../api';
import BarcodeScanner from '../../components/BarcodeScanner';
import NutritionLabelPhotoScan from '../../components/NutritionLabelPhotoScan';
import AddFoodModal from '../../components/AddFoodModal';
import { round1, SourceTag, FoodAutocomplete, QuantityPicker, pairedName } from '../../components/FoodPicker';

// Own copy of the meal list (same convention as the rest of the codebase).
const MEALS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snacks' },
];

// LOCAL calendar date (not UTC) — same reasoning as PlayerDataContext's tod().
const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function shiftDate(str, delta) {
  const d = new Date(str + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(str) {
  return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}
// round1, SourceTag, FoodAutocomplete, QuantityPicker: imported from ../../components/FoodPicker
// (extracted there so the chef recipe editor can reuse the exact same food search / quantity UI).

// English display name for a detected portion word (server.staging.js's PORTION_UNIT_WORDS
// concept key, e.g. "2 plakken kaas" -> {concept:'slice', quantity:2}) — shown in the "Check
// amounts" step before the line has been matched to a specific food, so grams can't be shown
// yet. Deliberately only used for display; the resolved gram amount is always what gets saved.
const PORTION_CONCEPT_EN = {
  slice: 'slice', tablespoon: 'tablespoon', teaspoon: 'teaspoon', piece: 'piece', handful: 'handful',
  cup: 'cup', bowl: 'bowl', glass: 'glass', portion: 'portion', can: 'can', bag: 'bag', scoop: 'scoop', package: 'pack',
};
function portionConceptLabel(portion) {
  const noun = PORTION_CONCEPT_EN[portion.concept] || portion.concept;
  const plural = portion.quantity === 1 ? noun : (noun === 'glass' ? 'glasses' : `${noun}s`);
  return `${portion.quantity}× ${plural}`;
}
function pluralizeConceptNoun(concept, quantity) {
  const noun = PORTION_CONCEPT_EN[concept] || concept;
  return quantity === 1 ? noun : (noun === 'glass' ? 'glasses' : `${noun}s`);
}
// "6 pieces of cherries" / "3 tablespoons of peanut butter" — names the CONCRETE interpretation
// itself (quantity + unit + food), not a generic "does this look right?" — assumes recognition
// is correct by default and asks a direct yes/no question about the one specific reading, per
// the player's own report that a vague confirmation didn't give them enough to actually judge.
function portionInterpretationText(portion, foodName) {
  return `${portion.quantity} ${pluralizeConceptNoun(portion.concept, portion.quantity)} of ${foodName}`;
}
const CARD = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 };
const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});

// ── One logged line ─────────────────────────────────────────────────────────
function EntryRow({ entry, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [grams, setGrams] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // A logged scoop/unit count from the chef's planned meal isn't a real diary_entries row — it's
  // derived live from nutrition_meal_scoop_logs (server.staging.js's computeLoggedScoopEntries),
  // so it has no id this page's PUT/DELETE routes could act on. Editing/removing it happens via
  // the count field on the "Meal of the day" card instead.
  const isChefMeal = entry.food_source === 'chef_meal';

  const save = async () => {
    if (!(grams > 0)) { setErr('Enter an amount greater than 0'); return; }
    setBusy(true); setErr('');
    try { await onUpdate(entry.id, { amount_grams: grams }); setEditing(false); }
    catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div style={{ padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          {entry.food_name}<SourceTag source={entry.food_source} />
        </div>
        <QuantityPicker food={entry} initialGrams={entry.amount_grams} onChange={setGrams} lang="en" />
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button style={btn(true)} onClick={save} disabled={busy}><Check size={13} /> Save</button>
          <button style={btn(false)} onClick={() => { setEditing(false); setErr(''); }}><X size={13} /></button>
        </div>
        {err && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{err}</div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {entry.food_name}<SourceTag source={entry.food_source} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {isChefMeal ? `${entry.scoop_count} ${entry.unit_label}${entry.scoop_count === 1 ? '' : 's'} · ` : ''}
          {entry.amount_grams} g
          {entry.grams_per_portion ? ` · ${round1(entry.amount_grams / entry.grams_per_portion)} portion` : ''}
          {' '}· {entry.kcal} kcal · P {entry.protein_g} / C {entry.carbs_g} / F {entry.fat_g} g
        </div>
      </div>
      {!isChefMeal && (
        <>
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setEditing(true)} title="Edit"><Pencil size={12} /></button>
          <button style={{ ...btn(false, '#ef4444'), padding: 6 }} onClick={() => onDelete(entry.id)} title="Delete"><Trash2 size={12} /></button>
        </>
      )}
    </div>
  );
}

// ── One meal section (Breakfast / Lunch / …) ────────────────────────────────
// plannedRecipes: the chef's day-recipes assigned to THIS meal moment (already filtered by the
// caller) — shown at the top of the section, with their scoop/unit logging inputs right there,
// instead of on a separate "Meal of the day" card the player had to go find first.
function MealSection({ meal, entries, plannedRecipes, playerId, onLogged, onAdd, onUpdate, onDelete, templates, onTemplatesChanged }) {
  const [adding, setAdding] = useState(false);
  // "Bewaar als favoriet" — saves whatever's currently logged for THIS meal+day as a reusable,
  // player-owned template (never tied to the meal moment it was saved from — see
  // nutrition_meal_templates' own comment). savingTemplate just toggles the inline name prompt;
  // the actual save reads visibleRows at click time, not some separately-tracked snapshot.
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateErr, setTemplateErr] = useState('');
  const [manageTemplates, setManageTemplates] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [applyingTemplateId, setApplyingTemplateId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [picked, setPicked] = useState(null);
  const [grams, setGrams] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Same raw-vs-cooked amount check the chef's recipe editor already has (server.staging.js's
  // detectQuantityStateNote via GET food-classification) — e.g. rice/pasta/potatoes have very
  // different macros per 100g depending on whether the raw or cooked NEVO item was picked, so a
  // player guessing the wrong one would silently log the wrong amount. Fetched fresh whenever a
  // food is picked (any path: search, barcode, photo, or "add new product").
  const [quantityNote, setQuantityNote] = useState(null);
  // Set only when the scanned barcode genuinely has no product data anywhere (not_found /
  // incomplete) — offers the photo-of-the-label fallback. A transient lookup problem (rate
  // limit, network) gets a plain retry message instead; that's not "unrecognised".
  const [unrecognizedBarcode, setUnrecognizedBarcode] = useState(null);
  const [showPhotoScan, setShowPhotoScan] = useState(false);
  // Not barcode-anchored at all — a product with no barcode, found nowhere yet. Same shared
  // nutrition_foods database as the barcode/photo-scan paths above (source='player'), so other
  // players/chefs/admins find it afterwards without re-entering it themselves.
  const [showAddFood, setShowAddFood] = useState(false);
  // "Paste a list" — the same two-step free-text recognition RecipeEditor.js already has for
  // chef recipes (check-ingredient-lines, then parse-ingredients), reused as-is here so a player
  // can log several foods from one pasted list instead of searching one at a time. A visible
  // extra row ABOVE the search box, never replacing it.
  const [bulkText, setBulkText] = useState('');
  const [checkedLines, setCheckedLines] = useState(null); // null | [{raw, name, amount_grams}]
  const [matchedLines, setMatchedLines] = useState(null); // null | [{raw, name, amount_grams, food, matched_via, quantity_state_note, error}]
  const [checkingBulk, setCheckingBulk] = useState(false);
  const [matchingBulk, setMatchingBulk] = useState(false);
  const [addingBulk, setAddingBulk] = useState(false);
  const [bulkErr, setBulkErr] = useState('');

  const rows = entries.filter(e => e.meal_type === meal.id);
  // A logged scoop/unit count already shows (and is editable) in the PlannedMealBlock above —
  // its diary-merge line (computeLoggedScoopEntries) still counts toward kcal, just not shown
  // twice as a separate manual-looking row here.
  const visibleRows = rows.filter(e => e.food_source !== 'chef_meal');
  const kcal = rows.reduce((s, e) => s + e.kcal, 0);

  const reset = () => {
    setAdding(false); setPicked(null); setGrams(null); setErr(''); setUnrecognizedBarcode(null); setQuantityNote(null);
    setBulkText(''); setCheckedLines(null); setMatchedLines(null); setBulkErr('');
  };

  // Step 1: parse the pasted text into lines + gram amounts only — no NEVO/AI yet, same as
  // RecipeEditor.js's checkBulk for the chef.
  const checkBulk = async () => {
    setBulkErr('');
    if (!bulkText.trim()) return;
    setCheckingBulk(true);
    try {
      const { lines } = await checkIngredientLines(bulkText);
      setCheckedLines(lines.map(l => ({ raw: l.raw, name: l.name, amount_grams: l.amount_grams, portion: l.portion || null, ambiguous: l.ambiguous || null, bareCount: l.bareCount || null })));
    } catch (e) {
      setBulkErr(e.message || 'Checking amounts failed.');
    } finally { setCheckingBulk(false); }
  };
  const setCheckedAmount = (i, g) => setCheckedLines(list => list.map((l, idx) => idx === i ? { ...l, amount_grams: g } : l));
  const removeCheckedLine = (i) => setCheckedLines(list => list.filter((_, idx) => idx !== i));

  // Step 2: match each checked line against NEVO — same as RecipeEditor.js's matchBulk, except
  // the result is a review list the player then logs in one go (addAllMatched), not rows added
  // to an in-memory recipe draft. `quantityNote` is renamed from the API's `quantity_state_note`
  // on the way in so it can be mutated per-line the same way RecipeEditor.js's ingredients are
  // (applyBulkQuantityConversion below swaps it out when the raw/cooked variant is switched).
  const matchBulk = async () => {
    setBulkErr('');
    if (!checkedLines || !checkedLines.length || checkedLines.some(l => !(l.amount_grams > 0) && !l.portion && !l.ambiguous && !l.bareCount)) return;
    setMatchingBulk(true);
    try {
      const { lines } = await parseRecipeIngredients(checkedLines);
      // `confirmed` tracks the yes/adjust step required for anything that wasn't a plain gram
      // amount (portion word, ambiguous word, resolved bare count) — see the "Does this look
      // right?" banner below. Plain-gram lines never needed confirming, so they start confirmed.
      setMatchedLines(lines.map(l => ({ ...l, quantityNote: l.quantity_state_note || null, confirmed: !l.needs_confirm })));
      setCheckedLines(null);
    } catch (e) {
      setBulkErr(e.message || 'Recognising foods failed.');
    } finally { setMatchingBulk(false); }
  };
  const removeMatchedLine = (i) => setMatchedLines(list => list.filter((_, idx) => idx !== i));
  // NOT also marking `confirmed: true` here is deliberate, even though a player editing the
  // amount by hand clearly IS taking explicit control of it: QuantityPicker fires this same
  // onChange once on mount too, just to push its own current value back up — that fire is not
  // the player doing anything, so treating every call as "confirmed" was quietly auto-confirming
  // (and silently locking in whichever measure happened to be selected by default) the instant
  // the picker appeared, before the player had seen or agreed to anything. Confirming only ever
  // happens through the explicit confirmMatchedLine/chooseAmbiguousOption button clicks below.
  const setMatchedAmount = (i, grams) => setMatchedLines(list => list.map((l, idx) => idx === i ? { ...l, amount_grams: grams } : l));
  const confirmMatchedLine = (i) => setMatchedLines(list => list.map((l, idx) => idx === i ? { ...l, confirmed: true } : l));
  // The player picks which ambiguous unit they meant (e.g. "lepel" -> tablespoon or teaspoon) —
  // never guessed server-side. Each option already carries its own resolved gram amount (the
  // server computed every candidate up front), so this is a single click, no extra round-trip.
  const chooseAmbiguousOption = (i, option) => setMatchedLines(list => list.map((l, idx) => idx === i
    ? { ...l, amount_grams: option.grams, portion: { concept: option.concept, quantity: option.quantity }, ambiguous_options: null, confirmed: true }
    : l));
  // Opts out of picking tablespoon-vs-teaspoon (or any ambiguous reading) entirely and drops
  // straight into the normal QuantityPicker instead — amount_grams stays null, so it mounts in
  // measure-pick mode (same "initialGrams == null" rule every picker already uses), letting the
  // player choose a completely different measure, or just type grams. Clearing ambiguous_options
  // (rather than leaving it set) is what lets QuantityPicker mount at all — see the "never mount
  // while ambiguous" guard on its render below.
  const chooseMeasureInstead = (i) => setMatchedLines(list => list.map((l, idx) => idx === i
    ? { ...l, ambiguous_options: null, confirmed: true }
    : l));

  // Switches one matched line to its raw/cooked paired food + the converted amount in one step —
  // mirrors RecipeEditor.js's applyQuantityConversion for the chef, per-line instead of per-key.
  const applyBulkQuantityConversion = async (i, note, convertedGrams) => {
    if (!note.paired_food) return;
    const newFood = { id: note.paired_food.id, name: note.paired_food.name, source: 'nevo' };
    setMatchedLines(list => list.map((l, idx) => idx === i ? { ...l, food: newFood, amount_grams: Math.round(convertedGrams), quantityNote: null, confirmed: true } : l));
    try {
      const cls = await getFoodClassification(newFood.id);
      setMatchedLines(list => list.map((l, idx) => idx === i ? { ...l, quantityNote: cls.quantity_state_note || null } : l));
    } catch { /* ignore */ }
  };

  // Logs every successfully-matched, ready-to-log line as a real diary entry, one at a time
  // (same onAdd the single-item "Add" button uses) — unmatched lines are simply skipped, the
  // player already saw them flagged as "No match" and could remove or ignore them. "Ready"
  // excludes anything still awaiting the player's explicit confirm/choice (needs_confirm &&
  // !confirmed, or an unresolved ambiguous unit) — those just aren't counted yet, never silently
  // logged with a guessed amount.
  const addAllMatched = async () => {
    const toAdd = (matchedLines || []).filter(l => l.food && l.amount_grams > 0 && (!l.needs_confirm || l.confirmed));
    if (!toAdd.length) return;
    setAddingBulk(true); setBulkErr('');
    try {
      for (const l of toAdd) {
        await onAdd({ meal_type: meal.id, food_id: l.food.id, amount_grams: l.amount_grams });
      }
      reset();
    } catch (e) {
      setBulkErr(e.message || 'Adding items failed.');
    } finally { setAddingBulk(false); }
  };

  // Reads visibleRows AT CLICK TIME (not a separately-tracked snapshot) — exactly what's
  // currently logged for this meal+day becomes the template, whatever that happens to be.
  const confirmSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    const items = visibleRows.map(e => ({ food_id: e.food_id, amount_grams: e.amount_grams }));
    if (!items.length) return;
    setTemplateBusy(true); setTemplateErr('');
    try {
      await saveMealTemplate(name, items);
      setSavingTemplate(false); setTemplateName('');
      onTemplatesChanged();
    } catch (e) {
      setTemplateErr(e.message || 'Saving failed.');
    } finally { setTemplateBusy(false); }
  };

  // Applying a template logs every one of its items as a new diary entry for THIS meal+day —
  // same one-at-a-time onAdd loop addAllMatched already uses for "Add N items", no extra
  // confirmation step, exactly as the feature asks for.
  const applyTemplate = async (template) => {
    setApplyingTemplateId(template.id);
    try {
      for (const it of template.items) {
        await onAdd({ meal_type: meal.id, food_id: it.food_id, amount_grams: it.amount_grams });
      }
    } finally { setApplyingTemplateId(null); }
  };

  const startRenameTemplate = (template) => { setRenamingId(template.id); setRenameValue(template.name); };
  const confirmRenameTemplate = async () => {
    const name = renameValue.trim();
    if (!name || renamingId == null) { setRenamingId(null); return; }
    try { await renameMealTemplate(renamingId, name); onTemplatesChanged(); }
    catch (e) { setTemplateErr(e.message || 'Renaming failed.'); }
    finally { setRenamingId(null); }
  };
  const removeTemplate = async (template) => {
    try { await deleteMealTemplate(template.id); onTemplatesChanged(); }
    catch (e) { setTemplateErr(e.message || 'Deleting failed.'); }
  };

  // Single entry point for "a food was picked", from any of the 4 ways one can land here (search,
  // barcode, photo scan, new product) — always re-fetches the raw/cooked note for the newly
  // picked food, same as RecipeEditor.js's fetchFoodMeta does for the chef.
  const pickFood = async (f) => {
    setPicked(f); setErr(''); setQuantityNote(null);
    try {
      const cls = await getFoodClassification(f.id);
      if (cls.quantity_state_note) setQuantityNote(cls.quantity_state_note);
    } catch { /* no classification available — not fatal, just no note shown */ }
  };

  const save = async () => {
    if (!picked) { setErr('Pick a food first'); return; }
    if (!(grams > 0)) { setErr('Enter an amount greater than 0'); return; }
    setBusy(true); setErr('');
    try { await onAdd({ meal_type: meal.id, food_id: picked.id, amount_grams: grams }); reset(); }
    catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  // Switches to the raw/cooked paired food and applies the converted amount in one step — mirrors
  // RecipeEditor.js's applyQuantityConversion for the chef.
  const applyQuantityConversion = async (note, convertedGrams) => {
    if (!note.paired_food) return;
    const newFood = { id: note.paired_food.id, name: note.paired_food.name, source: 'nevo' };
    setPicked(newFood); setGrams(Math.round(convertedGrams)); setQuantityNote(null);
    try {
      const cls = await getFoodClassification(newFood.id);
      if (cls.quantity_state_note) setQuantityNote(cls.quantity_state_note);
    } catch { /* ignore */ }
  };

  const onScanned = async (barcode) => {
    setScanning(false);
    setAdding(true); setPicked(null); setErr('Looking up barcode…'); setUnrecognizedBarcode(null); setQuantityNote(null);
    try {
      const r = await lookupBarcode(barcode);
      if (r.ok && r.food) { await pickFood(r.food); return; }
      if (r.status === 429) setErr('Barcode service is busy — wait a moment and try again, or search manually.');
      else if (r.error === 'incomplete') { setErr('Found the product but it has no usable nutrition data.'); setUnrecognizedBarcode(barcode); }
      else if (r.error === 'not_found') { setErr('Barcode not recognised.'); setUnrecognizedBarcode(barcode); }
      else setErr('Barcode lookup failed — search for the food manually.');
    } catch {
      setErr('Barcode lookup failed — search for the food manually.');
    }
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{meal.label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{kcal} kcal</span>
      </div>

      {plannedRecipes.map(r => (
        <PlannedMealBlock key={r.day_recipe_id} recipe={r} playerId={playerId} onLogged={onLogged} />
      ))}

      {visibleRows.length === 0 && plannedRecipes.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: 'var(--text-dimmed)', marginBottom: 8 }}>Nothing added yet.</div>
      )}
      {visibleRows.map(e => <EntryRow key={e.id} entry={e} onUpdate={onUpdate} onDelete={onDelete} />)}

      {visibleRows.length > 0 && !savingTemplate && (
        <button type="button" onClick={() => { setSavingTemplate(true); setTemplateName(''); setTemplateErr(''); }}
          style={{ ...btn(false), fontSize: 11, padding: '3px 8px', marginBottom: 8 }}>
          Save as favorite
        </button>
      )}
      {savingTemplate && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Name this meal…"
            style={{ ...inputStyle, width: 160 }} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') confirmSaveTemplate(); }} />
          <button onClick={confirmSaveTemplate} disabled={templateBusy || !templateName.trim()} style={{ ...btn(true), fontSize: 11, padding: '4px 9px' }}>
            {templateBusy ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => setSavingTemplate(false)} style={{ ...btn(false), fontSize: 11, padding: '4px 9px' }}>Cancel</button>
        </div>
      )}
      {templateErr && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{templateErr}</div>}

      {adding ? (
        <div style={{ padding: '10px', background: 'var(--bg-app)', borderRadius: 8, marginTop: 4 }}>
          {!picked ? (
            <>
              {templates.length > 0 && (
                <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Use a saved meal</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value="" disabled={!!applyingTemplateId}
                      onChange={e => { const t = templates.find(x => String(x.id) === e.target.value); if (t) applyTemplate(t); }}
                      style={{ ...inputStyle, width: 'auto', flex: 1 }}>
                      <option value="" disabled>{applyingTemplateId ? 'Adding…' : 'Choose a saved meal…'}</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.items.length} item{t.items.length === 1 ? '' : 's'})</option>)}
                    </select>
                    <button type="button" onClick={() => setManageTemplates(m => !m)} style={{ ...btn(false), padding: '4px 8px', fontSize: 11 }}>
                      {manageTemplates ? 'Done' : 'Manage'}
                    </button>
                  </div>
                  {manageTemplates && (
                    <div style={{ marginTop: 8 }}>
                      {templates.map(t => (
                        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                          {renamingId === t.id ? (
                            <>
                              <input value={renameValue} onChange={e => setRenameValue(e.target.value)}
                                style={{ ...inputStyle, flex: 1, padding: '4px 6px' }} autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') confirmRenameTemplate(); }} />
                              <button onClick={confirmRenameTemplate} style={{ ...btn(true), padding: '3px 6px' }}><Check size={11} /></button>
                              <button onClick={() => setRenamingId(null)} style={{ ...btn(false), padding: '3px 6px' }}><X size={11} /></button>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: 12, flex: 1 }}>{t.name}</span>
                              <button onClick={() => startRenameTemplate(t)} style={{ ...btn(false), padding: '3px 6px' }} title="Rename"><Pencil size={11} /></button>
                              <button onClick={() => removeTemplate(t)} style={{ ...btn(false, '#ef4444'), padding: '3px 6px' }} title="Delete"><Trash2 size={11} /></button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Paste a list</div>
                {checkedLines === null && matchedLines === null && (
                  <>
                    <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
                      style={{ ...inputStyle, minHeight: 56, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                      placeholder={'One food per line, always in grams, e.g.\n300 g chicken\nrice 200 g'} />
                    <button onClick={checkBulk} disabled={checkingBulk || !bulkText.trim()} style={{ ...btn(true), marginTop: 6, fontSize: 12, padding: '5px 10px' }}>
                      {checkingBulk ? 'Checking…' : 'Check amounts'}
                    </button>
                  </>
                )}
                {checkedLines !== null && (() => {
                  const missing = checkedLines.filter(l => !(l.amount_grams > 0) && !l.portion && !l.ambiguous && !l.bareCount);
                  return (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
                        {missing.length
                          ? 'Fill in a gram amount (or a portion, like "2 slices") for each line first — then they can be recognised.'
                          : 'Every line has an amount. Ready to recognise the foods.'}
                      </div>
                      {checkedLines.map((l, i) => {
                        const hasAmount = l.amount_grams > 0 || !!l.portion || !!l.ambiguous || !!l.bareCount;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 7px', marginBottom: 4, borderRadius: 6, background: 'var(--bg-card)', border: `1px solid ${hasAmount ? 'transparent' : '#ef4444'}` }}>
                            <span style={{ fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.raw}>{l.raw}</span>
                            {l.portion ? (
                              <span style={{ fontSize: 11, color: 'var(--text-dimmed)', whiteSpace: 'nowrap' }} title="Grams are calculated once this is matched to a food">
                                {portionConceptLabel(l.portion)}
                              </span>
                            ) : l.ambiguous ? (
                              <span style={{ fontSize: 11, color: '#f59e0b', whiteSpace: 'nowrap' }} title="Which one you meant is asked once this is matched to a food">
                                {l.ambiguous.quantity}× spoon (?)
                              </span>
                            ) : l.bareCount ? (
                              <span style={{ fontSize: 11, color: 'var(--text-dimmed)', whiteSpace: 'nowrap' }} title="Treated as a piece count — grams are calculated once this is matched to a food">
                                {l.bareCount.quantity}× pieces?
                              </span>
                            ) : (
                              <input type="number" min="0" step="1" value={l.amount_grams ?? ''}
                                onChange={e => setCheckedAmount(i, e.target.value ? Number(e.target.value) : null)}
                                style={{ ...inputStyle, width: 64, padding: '4px 6px' }} placeholder="grams" />
                            )}
                            <button onClick={() => removeCheckedLine(i)} style={{ ...btn(false, '#ef4444'), padding: '3px 6px' }} title="Remove"><Trash2 size={11} /></button>
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button onClick={matchBulk} disabled={matchingBulk || !checkedLines.length || missing.length > 0} style={{ ...btn(true), fontSize: 12, padding: '5px 10px' }}>
                          {matchingBulk ? 'Recognising…' : 'Recognise foods'}
                        </button>
                        <button onClick={() => setCheckedLines(null)} style={{ ...btn(false), fontSize: 12, padding: '5px 10px' }}>Cancel</button>
                      </div>
                    </div>
                  );
                })()}
                {matchedLines !== null && (() => {
                  const matched = matchedLines.filter(l => l.food);
                  const ready = matchedLines.filter(l => l.food && l.amount_grams > 0 && (!l.needs_confirm || l.confirmed));
                  return (
                    <div>
                      {/* "Product recognition", not "Ingredients" — that's a recipe-editor term, and
                          this is the player's own food diary, not a recipe. Otherwise mirrors
                          RecipeEditor.js's "Ingredients" block exactly: full QuantityPicker (named
                          measures, "choose a measure instead") + the raw/cooked conversion banner. */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>
                        Product recognition
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
                        {matched.length} of {matchedLines.length} recognised — review below, then add them.
                      </div>
                      {matchedLines.map((l, i) => (
                        <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 8, marginBottom: 6, border: `1px solid ${l.food ? 'transparent' : '#ef4444'}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              {l.food ? <>{pairedName(l.food)}<SourceTag source={l.food.source} /></> : <em style={{ color: '#ef4444', fontWeight: 400 }}>No match: {l.raw}</em>}
                            </span>
                            <button onClick={() => removeMatchedLine(i)} style={{ ...btn(false, '#ef4444'), padding: '3px 6px', flexShrink: 0 }} title="Remove"><Trash2 size={11} /></button>
                          </div>
                          {l.food && l.ambiguous_options && !l.confirmed && (
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>
                                  Do you mean {l.ambiguous_options.length > 0
                                    ? l.ambiguous_options.map(opt => `${l.ambiguous_quantity} ${pluralizeConceptNoun(opt.concept, l.ambiguous_quantity)}`).join(' or ')
                                    : `a different amount`} of {pairedName(l.food)}?
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: l.ambiguous_options.length > 0 ? 6 : 0 }}>
                                {l.ambiguous_options.length > 0 ? l.ambiguous_options.map(opt => (
                                  <button key={opt.concept} type="button" onClick={() => chooseAmbiguousOption(i, opt)} style={{ ...btn(false), fontSize: 11, padding: '4px 8px' }}>
                                    {PORTION_CONCEPT_EN[opt.concept] || opt.concept} · {opt.grams}g
                                  </button>
                                )) : null}
                              </div>
                              <button type="button" onClick={() => chooseMeasureInstead(i)} style={{ ...btn(false), fontSize: 11, padding: '4px 8px' }}>
                                {l.ambiguous_options.length > 0 ? 'or choose a measure instead' : 'choose a measure instead'}
                              </button>
                            </div>
                          )}
                          {l.food && l.needs_confirm && !l.confirmed && !l.ambiguous_options && l.amount_grams > 0 && (
                            <div style={{ marginBottom: 8, padding: '6px 8px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)' }}>
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                Do you mean {portionInterpretationText(l.portion, pairedName(l.food))}? That's {l.amount_grams}g.
                              </div>
                              <button type="button" onClick={() => confirmMatchedLine(i)} style={{ ...btn(true), fontSize: 11, padding: '3px 9px' }}>
                                Yes, {l.amount_grams}g
                              </button>
                            </div>
                          )}
                          {/* While an ambiguous unit is still unresolved, QuantityPicker must not mount at all —
                              it would fire its own onChange with whatever measure it defaults to, overwriting
                              amount_grams before the player has actually chosen tablespoon vs teaspoon. */}
                          {l.food && !(l.ambiguous_options && !l.confirmed) && (
                            <>
                              <QuantityPicker key={`${i}-${l.food.id}`} food={l.food} initialGrams={l.amount_grams} onChange={g => setMatchedAmount(i, g)} lang="en" />
                              {l.quantityNote && l.amount_grams > 0 && (() => {
                                const note = l.quantityNote;
                                const converted = note.state === 'raw' ? l.amount_grams * note.factor : l.amount_grams / note.factor;
                                const convertedRounded = Math.round(converted);
                                const otherStateLabel = note.state === 'raw' ? 'cooked/fried' : 'raw';
                                return (
                                  <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b' }}>
                                    <div style={{ fontSize: 11, color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                                      <span>
                                        This is the {note.state === 'raw' ? 'raw' : 'cooked/fried'} variant of {note.category} (factor {note.factor}x raw&rarr;cooked).
                                        Did you mean the {otherStateLabel} amount? That would be ~{convertedRounded} g.
                                      </span>
                                    </div>
                                    {note.paired_food ? (
                                      <button type="button" onClick={() => applyBulkQuantityConversion(i, note, converted)} style={{ ...btn(false), marginTop: 6, padding: '3px 8px', fontSize: 11 }}>
                                        Use {note.paired_food.name} · {convertedRounded} g
                                      </button>
                                    ) : (
                                      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 4 }}>
                                        No exact match found for the {otherStateLabel} variant — search for it yourself if that's what you meant.
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      ))}
                      {matched.length > 0 && ready.length < matched.length && (
                        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 4 }}>
                          {matched.length - ready.length} line{matched.length - ready.length === 1 ? '' : 's'} still need{matched.length - ready.length === 1 ? 's' : ''} your confirmation above before they can be added.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button onClick={addAllMatched} disabled={addingBulk || !ready.length} style={{ ...btn(true), fontSize: 12, padding: '5px 10px' }}>
                          {addingBulk ? 'Adding…' : `Add ${ready.length} item${ready.length === 1 ? '' : 's'}`}
                        </button>
                        <button onClick={() => { setMatchedLines(null); setBulkText(''); }} style={{ ...btn(false), fontSize: 12, padding: '5px 10px' }}>Cancel</button>
                      </div>
                    </div>
                  );
                })()}
                {bulkErr && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>{bulkErr}</div>}
              </div>

              <FoodAutocomplete onPick={pickFood} />
              <button type="button" onClick={() => setShowAddFood(true)} style={{ ...btn(false), marginTop: 6, fontSize: 11, padding: '4px 8px' }}>
                Not found? Add a new product…
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {picked.name}<SourceTag source={picked.source} />
                <button onClick={() => { setPicked(null); setGrams(null); setQuantityNote(null); }} style={{ ...btn(false), padding: '2px 8px', marginLeft: 8, fontSize: 11 }}>change</button>
              </div>
              <QuantityPicker food={picked} onChange={setGrams} lang="en" />
              {quantityNote && grams > 0 && (() => {
                const note = quantityNote;
                const converted = note.state === 'raw' ? grams * note.factor : grams / note.factor;
                const convertedRounded = Math.round(converted);
                const otherStateLabel = note.state === 'raw' ? 'cooked/fried' : 'raw';
                return (
                  <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b' }}>
                    <div style={{ fontSize: 11, color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        This is the {note.state === 'raw' ? 'raw' : 'cooked/fried'} variant of {note.category} (factor {note.factor}x raw&rarr;cooked).
                        Did you mean the {otherStateLabel} amount? That would be ~{convertedRounded} g.
                      </span>
                    </div>
                    {note.paired_food ? (
                      <button type="button" onClick={() => applyQuantityConversion(note, converted)} style={{ ...btn(false), marginTop: 6, padding: '3px 8px', fontSize: 11 }}>
                        Use {note.paired_food.name} · {convertedRounded} g
                      </button>
                    ) : (
                      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 4 }}>
                        No exact match found for the {otherStateLabel} variant — search for it yourself if that's what you meant.
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {err && <div style={{ fontSize: 11, color: err.startsWith('Looking') ? 'var(--text-muted)' : '#ef4444', marginTop: 6 }}>{err}</div>}
          {unrecognizedBarcode && !picked && (
            <button style={{ ...btn(false, '#a855f7'), marginTop: 8 }} onClick={() => setShowPhotoScan(true)}>
              <Camera size={13} /> Take a photo of the nutrition label
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={btn(true)} onClick={save} disabled={busy || !picked || !(grams > 0)}>{busy ? 'Saving…' : 'Add'}</button>
            <button style={btn(false)} onClick={reset}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => { setErr(''); setAdding(true); }}>
            <Plus size={13} /> Add
          </button>
          <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setScanning(true)}>
            <ScanLine size={13} /> Scan
          </button>
        </div>
      )}

      {scanning && <BarcodeScanner onDetect={onScanned} onClose={() => setScanning(false)} />}
      {showPhotoScan && (
        <NutritionLabelPhotoScan
          barcode={unrecognizedBarcode}
          onClose={() => setShowPhotoScan(false)}
          onSaved={(food) => { pickFood(food); setUnrecognizedBarcode(null); setShowPhotoScan(false); }}
        />
      )}
      {showAddFood && (
        <AddFoodModal
          showClassify={false}
          lang="en"
          onClose={() => setShowAddFood(false)}
          onAdded={(food) => { pickFood(food); setShowAddFood(false); }}
        />
      )}
    </div>
  );
}

// ── Action plan set by staff — read-only for the player ─────────────────────
// Mirrors the admin Nutrition screen's Actieplan section — shows the MOST RECENT profile report
// (staff can save a new dated report any time; earlier ones stay in the admin history but are
// never shown here). Renders nothing at all when there's no plan yet, rather than an empty card.
function ActionPlanCard({ playerId }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getLatestNutritionProfileReport(playerId)
      .then(p => { if (!cancelled) setItems((p.action_plan || []).filter(x => String(x || '').trim())); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [playerId]);

  if (!items || items.length === 0) return null;
  return (
    <div style={CARD}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Action plan</div>
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)' }}>
        {items.map((text, i) => <li key={i} style={{ marginBottom: 4 }}>{text}</li>)}
      </ol>
    </div>
  );
}

// ── Day total vs goal ───────────────────────────────────────────────────────
function GoalBar({ label, value, goal, unit }) {
  const pct = goal ? Math.round((value / goal) * 100) : null;
  const width = pct == null ? 0 : Math.min(100, pct);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ color: 'var(--text-muted)' }}>
          {value}{unit}{goal != null ? ` / ${goal}${unit}` : ''}{pct != null ? ` · ${pct}%` : ''}
        </span>
      </div>
      {goal != null && (
        <div style={{ height: 6, background: 'var(--gauge-track)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: pct > 100 ? '#ef4444' : pct === 100 ? '#10b981' : '#3b82f6', borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

// ── Meal of the day — scoop logging (groundwork) ─────────────────────────────
// Shows whatever the chef assigned for this date's Plate (server.staging.js's
// GET /api/nutrition/player/meal-today) and lets the player log a count per scoop-station group
// the recipe actually has (server-computed scoop_groups_present) — the chef partitions the 4
// roles into however many groups they like (RecipeEditor.js's Opscheplepel-indeling), so this is
// never just a binary "one count or one per role" anymore. Each group shows a short, everyday
// English description of what's actually in it that day (server-generated via Claude, cached),
// not the literal NEVO product names. A group normally counts in "scoops", but the chef can set
// a specific named unit per group instead (e.g. "steak", "sweet potato") — group.unit_name.
// No gram/macro conversion is shown yet: scoop_grams (admin-set, per role) is still null until
// the real values are filled in — logging the count now means only the "connect the conversion"
// step is left once they are.
const SCOOP_ROLE_LABELS = { protein: 'Protein source', carbs: 'Carb source', vegetable: 'Vegetable source', other: 'Other' };

// Admin-configurable board label (e.g. "Bord 5" — Settings screens stay Dutch on purpose) shown
// to a player as "Plate 5" instead — a display-only transform, never touches the stored value.
function playerPlateLabel(board) {
  if (!board) return board;
  const m = String(board).match(/^bord\s*(.*)$/i);
  return m ? `Plate ${m[1]}`.trim() : board;
}

function pluralizeUnit(word, count) {
  if (!word) return word;
  return Number(count) === 1 ? word : (/[sxz]$|[cs]h$/i.test(word) ? `${word}es` : `${word}s`);
}

// perUnit (server-computed) is always present for a named-unit group (derived straight from this
// group's own ingredient grams/macros ÷ recipe_yield) and present for a plain scoop group too as
// soon as the admin has filled in a grams-per-scoop value for at least one of its roles
// (nutrition_scoop_grams) — server.staging.js's computeGroupPerCount. Only a scoop group whose
// roles have no admin value at all yet comes back with perUnit null, which is when the "not set
// up yet" caveat below applies.
function ScoopInput({ dayRecipeId, groupKey, description, roleLabel, unitName, perUnit, initialCount, playerId, onSaved }) {
  const [count, setCount] = useState(initialCount != null ? String(initialCount) : '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (count === '' || Number(count) < 0) return;
    setSaving(true); setSaved(false);
    try {
      await logMealScoop(dayRecipeId, groupKey, Number(count), playerId);
      setSaved(true);
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  const unitWord = pluralizeUnit(unitName || 'scoop', count || 0);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{description || roleLabel}</div>
      {description && <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginBottom: 4 }}>{roleLabel}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <input type="number" min="0" step="1" value={count} onChange={e => { setCount(e.target.value); setSaved(false); }}
          style={{ ...inputStyle, width: 64 }} placeholder="0" />
        <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>{unitWord}</span>
        <button style={{ ...btn(false), padding: '5px 9px' }} disabled={saving || count === ''} onClick={save}>
          {saving ? '…' : saved ? <Check size={13} /> : 'Log'}
        </button>
      </div>
      {perUnit && (
        <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
          ≈ {perUnit.grams}g per {unitWord.replace(/s$/, '')} · {perUnit.kcal} kcal · P {perUnit.protein_g}g / C {perUnit.carbs_g}g / F {perUnit.fat_g}g
        </div>
      )}
    </div>
  );
}

// One recipe the chef planned for this meal moment, shown right inside that meal's own section
// (MealSection) — a player logging their scoop/unit count here needs no separate card or tab.
function PlannedMealBlock({ recipe, playerId, onLogged }) {
  const hasUnconvertedScoopGroup = recipe.scoop_groups_present.some(g => !g.unit_name && !g.per_unit);
  return (
    <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: 10, marginBottom: 8, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
        Planned meal
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{recipe.name}</div>
      {recipe.scoop_groups_present.map(group => (
        <ScoopInput key={group.key} dayRecipeId={recipe.day_recipe_id} groupKey={group.key}
          description={group.description}
          roleLabel={group.roles.map(role => SCOOP_ROLE_LABELS[role]).join(' + ')}
          unitName={group.unit_name}
          perUnit={group.per_unit}
          initialCount={recipe.logged[group.key]} playerId={playerId} onSaved={onLogged} />
      ))}
      {hasUnconvertedScoopGroup && (
        <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 2 }}>
          Scoop counts — the gram/macro conversion isn&apos;t set up yet.
        </div>
      )}
    </div>
  );
}

export default function PlayerNutrition({ playerRecord }) {
  const navigate = useNavigate();
  const [date, setDate] = useState(tod());
  const [data, setData] = useState({
    entries: [],
    totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, saturated_fat_g: 0, unsaturated_fat_g: 0 },
    goal: null,
    effectiveGoals: { kcal_goal: null, protein_g_goal: null, carbs_g_goal: null, fat_g_goal: null },
  });
  const [loading, setLoading] = useState(true);
  // The chef's planned meal(s) for this date, split per meal moment below so each MealSection
  // can show (and let the player log against) only what's actually assigned to it.
  const [mealPlan, setMealPlan] = useState(null);
  // Saved meal templates ("Bewaar als favoriet") — one shared list across all 4 MealSections
  // (a template isn't tied to any one meal moment, so there's no reason to fetch it 4 times);
  // the server resolves ownership from the session itself, nothing player-specific to pass here.
  const [templates, setTemplates] = useState([]);
  const loadTemplates = useCallback(() => {
    getMealTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // showLoading is only true for the genuine first load of a date (nothing sensible to show yet,
  // so the spinner replacing MEALS.map(...) is correct there). Every other refresh — after
  // logging a scoop/unit count, adding/editing/deleting a manual entry — must NOT flip the
  // top-level `loading` flag: that flag gates the whole MEALS.map(...) block below (including
  // every PlannedMealBlock/ScoopInput), so toggling it on a background refresh was unmounting and
  // remounting the entire block the player just interacted with — the "Log" button's own count
  // appeared to vanish (it hadn't; the block briefly became the "Loading…" placeholder instead,
  // then came back). Keeping `loading` untouched here lets the existing content stay on screen
  // (and each row's own busy/disabled state show the refresh instead) while data/mealPlan update
  // underneath it.
  const load = useCallback(async (showLoading = false) => {
    if (!playerRecord?.id) { setLoading(false); return; }
    if (showLoading) setLoading(true);
    try { setData(await getNutritionDiary(playerRecord.id, date)); }
    catch { /* leave last-loaded data in place */ }
    finally { if (showLoading) setLoading(false); }
  }, [playerRecord?.id, date]);

  const loadMealPlan = useCallback(() => {
    if (!playerRecord?.id) return;
    getPlayerMealToday(playerRecord.id, date).then(setMealPlan).catch(() => setMealPlan({ recipes: [] }));
  }, [playerRecord?.id, date]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { loadMealPlan(); }, [loadMealPlan]);
  // Logging a scoop/unit count changes both the diary totals (via the server-side merge) and
  // the plan's own "already logged" state — both need a fresh fetch afterwards, quietly (see
  // `load` above) so the block the player just logged against stays visible and editable.
  const reloadAfterLogging = () => { load(); loadMealPlan(); };

  const addEntry = async (payload) => {
    await addNutritionEntry({ player_id: playerRecord.id, entry_date: date, ...payload });
    load();
  };
  const updateEntry = async (id, patch) => { await updateNutritionEntry(id, patch); load(); };
  const removeEntry = async (id) => { await deleteNutritionEntry(id); load(); };

  const { totals } = data;
  const eg = data.effectiveGoals || {};
  const plannedRecipes = mealPlan?.recipes || [];

  return (
    <div style={{ padding: '14px 14px 24px', maxWidth: 640, margin: '0 auto' }}>
      <button onClick={() => navigate('/player/forms')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0, fontFamily: 'inherit' }}>
        <ChevronLeft size={16} /> Back
      </button>

      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>Food Tracker</div>

      {/* Date picker */}
      <div style={{ ...CARD, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(d => shiftDate(d, -1))}><ChevronLeft size={15} /></button>
        <input type="date" value={date} onChange={e => setDate(e.target.value || tod())}
          style={{ ...inputStyle, width: 'auto', flex: 1, textAlign: 'center' }} />
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(d => shiftDate(d, 1))}><ChevronRight size={15} /></button>
        <button style={btn(date === tod())} onClick={() => setDate(tod())}>Today</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-dimmed)', margin: '-4px 0 12px 2px' }}>
        {fmtDay(date)}
        {mealPlan?.label && (
          <span> · {mealPlan.label}{mealPlan.board ? ` — ${playerPlateLabel(mealPlan.board)}` : ''}</span>
        )}
      </div>

      {playerRecord?.id && <ActionPlanCard playerId={playerRecord.id} />}

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <>
          {MEALS.map(m => (
            <MealSection key={m.id} meal={m} entries={data.entries}
              plannedRecipes={plannedRecipes.filter(r => r.meal_type === m.id)}
              playerId={playerRecord?.id} onLogged={reloadAfterLogging}
              onAdd={addEntry} onUpdate={updateEntry} onDelete={removeEntry}
              templates={templates} onTemplatesChanged={loadTemplates} />
          ))}

          {/* Day total */}
          <div style={{ ...CARD, marginTop: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Day total</div>
            <GoalBar label="Calories" value={totals.kcal} goal={eg.kcal_goal} unit=" kcal" />
            <GoalBar label="Protein"  value={totals.protein_g} goal={eg.protein_g_goal} unit=" g" />
            <GoalBar label="Carbs"    value={totals.carbs_g} goal={eg.carbs_g_goal} unit=" g" />
            <GoalBar label="Fat"      value={totals.fat_g} goal={eg.fat_g_goal} unit=" g" />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 3px 12px' }}>
              <span>— saturated</span><span>{totals.saturated_fat_g ?? 0} g</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 8px 12px' }}>
              <span>— unsaturated</span><span>{totals.unsaturated_fat_g ?? 0} g</span>
            </div>
            {eg.kcal_goal == null && eg.fat_g_goal == null && (
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 4 }}>
                No calorie / fat goal set by staff yet.
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>
              Protein &amp; carb goals are calculated from your body weight and the day type in
              the week schedule.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
